import { Tooltip } from '@src/components/ui/tooltip';
import { cn } from '@src/lib/utils';

import useLangText from '@shared/hooks/useLangText';
import {
    ProcessFieldWithTableColumns,
    ServiceFieldTableRowDto,
    ServiceVM,
    TableFieldSelectionRecordVM,
} from '@shared/types/process';
import '@src/components/ui/table.css';

import { TableFieldFilterBar } from './TableFieldFilterBar';
import { TableFieldGrid } from './TableFieldGrid';
import { TableFieldToolbar } from './TableFieldToolbar';
import {
    createRowFromSelection,
    getLastPage,
    isEmptySelectionRecord,
    serializeTableRows,
    TableRowsQueryState,
} from './tableFieldUtils';
import { useTableFieldColumns } from './useTableFieldColumns';

import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

type Props = {
    field: ProcessFieldWithTableColumns;
    value?: ServiceFieldTableRowDto[] | { rows?: ServiceFieldTableRowDto[] };
    onChange?: (value: { rows: ServiceFieldTableRowDto[] }) => void;
    service?: ServiceVM;
    disabled?: boolean;
    readOnly?: boolean;
    label?: ReactNode;
    tooltip?: ReactNode;
    required?: boolean;
};

const DEFAULT_PAGE_SIZE = 10;

function normalizeRows(
    value?: ServiceFieldTableRowDto[] | { rows?: ServiceFieldTableRowDto[] },
): ServiceFieldTableRowDto[] {
    if (Array.isArray(value)) {
        return value;
    }

    return value?.rows ?? [];
}

export function TableFieldInner({
    field,
    value,
    onChange,
    service,
    disabled,
    readOnly,
    label,
    tooltip,
    required,
}: Props) {
    const getLangText = useLangText();
    const rows = normalizeRows(value);

    const { data: columns = [], isFetching } = useTableFieldColumns(field.id);

    const [queryState, setQueryState] = useState<TableRowsQueryState>({
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
    });

    const isFiltrationEnabled = field.options.enableFiltration === true;

    const [editRequest, setEditRequest] = useState<{
        rowId: string;
        focusFirstField: boolean;
    } | null>(null);

    const setRows = useCallback(
        (nextRows: ServiceFieldTableRowDto[]) => {
            onChange?.({ rows: serializeTableRows(nextRows) });
        },
        [onChange],
    );

    const handleAddRows = useCallback(
        (records: TableFieldSelectionRecordVM[]) => {
            const appended = records.map(createRowFromSelection);
            const nextRows = [...rows, ...appended];

            setRows(nextRows);

            if (records.length === 1 && isEmptySelectionRecord(records[0])) {
                const newRow = appended[0];
                const lastPage = getLastPage(
                    nextRows.length,
                    queryState.pageSize,
                );

                setQueryState((prev) =>
                    prev.page === lastPage ? prev : { ...prev, page: lastPage },
                );
                setEditRequest({
                    rowId: newRow.rowId,
                    focusFirstField: true,
                });
            }
        },
        [queryState.pageSize, rows, setRows],
    );

    return (
        <div
            className={cn(
                'table-field font-inter flex w-full min-w-0 flex-col gap-5 md:gap-4',
                readOnly && 'gap-2 md:gap-2',
            )}
        >
            {label != null && (
                <div
                    className={cn(
                        'flex items-start gap-1',
                        readOnly
                            ? 'text-xs leading-4 font-normal text-(--color-grey-text)'
                            : 'text-sm md:mb-1 md:leading-[21px]',
                    )}
                >
                    <span
                        className={readOnly ? undefined : 'table-field__label'}
                    >
                        {label}
                    </span>
                    {required && (
                        <span className='font-medium text-(--color-danger)'>
                            *
                        </span>
                    )}
                    {tooltip && (
                        <Tooltip content={tooltip}>
                            <Info className='table-field__icon-muted size-4' />
                        </Tooltip>
                    )}
                </div>
            )}

            {!readOnly && (
                <TableFieldToolbar
                    allowEmptyRow={columns.some(
                        (column) => column.fillSourceId === 'Manual',
                    )}
                    disabled={disabled}
                    fieldId={field.id}
                    title={label}
                    placeholder={
                        typeof label === 'string'
                            ? label
                            : getLangText(field.name)
                    }
                    rows={rows}
                    serviceId={service?.id}
                    tableOptions={field.options}
                    onAddRows={handleAddRows}
                />
            )}

            {isFetching ? (
                <div className='table-field__loading'>...</div>
            ) : (
                <>
                    {!readOnly && isFiltrationEnabled && columns.length > 0 && (
                        <TableFieldFilterBar
                            columns={columns}
                            queryState={queryState}
                            onQueryStateChange={setQueryState}
                        />
                    )}

                    <TableFieldGrid
                        columns={columns}
                        disabled={disabled}
                        queryState={queryState}
                        readOnly={readOnly}
                        requestEditRowId={editRequest?.rowId ?? null}
                        rows={rows}
                        requestFocusFirstField={
                            editRequest?.focusFirstField ?? false
                        }
                        onChangeRows={setRows}
                        onQueryStateChange={setQueryState}
                        onRequestEditHandled={() => setEditRequest(null)}
                    />
                </>
            )}
        </div>
    );
}
